/**
 * The island generator.
 *
 * One island a day, cached once and walked by everybody, so this must be a
 * pure function of the seed: same seed, same ground, for every player, every
 * replay and the daily job. The seed is published after finalisation and can
 * be checked against what was walked (`docs/rules.md` section 2).
 *
 * The guarantees the generator owes, each with a property test in
 * `generate.test.ts`:
 *
 *   - one connected landmass;
 *   - 2 to 4 ruins;
 *   - 3 to 6 relics;
 *   - at least 3 landing shores, pairwise far apart;
 *   - every ruin and every relic reachable from at least one shore;
 *   - no single shore reaches every relic within a day's stamina, so no one
 *     player can sweep the island alone -- the reason the letters matter;
 *   - determinism.
 *
 * The last one is the only guarantee that shapes the algorithm rather than
 * being a consequence of it. Relics are therefore not scattered and checked:
 * the shores are chosen first, and relics are placed to leave each shore
 * something it cannot reach.
 */

import { GAME_CONFIG, type Terrain } from '../game-config.ts'
import { createRng, type Rng } from '../rng.ts'
import { reachableWithin, type ReachMap } from '../reachability.ts'
import {
  chebyshev,
  index,
  inBounds,
  manhattan,
  neighbours,
  position,
  TILE_COUNT,
  type Position,
} from '../tiles.ts'

const { width, height } = GAME_CONFIG.grid
const {
  ruins: RUIN_RANGE,
  relics: RELIC_RANGE,
  shores: SHORE_RULE,
  shoreSeparation,
} = GAME_CONFIG.generator

/** An island as the day row caches it. Positions are tiles on the 20x12 grid. */
export type Island = {
  readonly width: number
  readonly height: number
  /** Row-major terrain, one entry per tile. */
  readonly tiles: readonly Terrain[]
  /** Landing shores, in the order the day assigns them by index. */
  readonly shores: readonly Position[]
  readonly ruins: readonly Position[]
  /** Relic tiles. The index into this list is the relic index a stake records. */
  readonly relics: readonly Position[]
}

/** How much of the grid may be land before an island stops being an island. */
const LAND_RANGE = { min: 85, max: 175 } as const

/** Attempts before the generator gives up on a seed. Reached by no seed tested. */
const MAX_ATTEMPTS = 200

export function terrainAt(island: Island, at: Position): Terrain {
  if (!inBounds(at)) throw new Error(`${at.x}, ${at.y} is not on the island`)
  return island.tiles[index(at)]!
}

/** The relic index at a tile, or `undefined`. Relic stakes are keyed by it. */
export function relicIndexAt(island: Island, at: Position): number | undefined {
  const found = island.relics.findIndex((relic) => relic.x === at.x && relic.y === at.y)
  return found === -1 ? undefined : found
}

export function isRuin(island: Island, at: Position): boolean {
  return terrainAt(island, at) === 'ruin'
}

/** Build the island for a seed. Deterministic, and the same for everyone. */
export function generateIsland(seed: Uint8Array): Island {
  const rng = createRng(seed)
  for (let attempt = 0; attempt < MAX_ATTEMPTS; attempt += 1) {
    const island = attemptIsland(rng)
    if (island !== undefined) return island
  }
  // Every attempt draws fresh numbers, so this means the shape rules are
  // mutually unsatisfiable rather than that this seed was unlucky.
  throw new Error('the generator could not satisfy its own guarantees')
}

// --- one attempt -----------------------------------------------------------

function attemptIsland(rng: Rng): Island | undefined {
  const land = growLandmass(rng)
  if (land === undefined) return undefined

  const depth = distanceFromWater(land)
  const tiles = paintTerrain(rng, land, depth)

  const shores = chooseShores(rng, tiles)
  if (shores === undefined) return undefined

  const ruins = placeRuins(rng, tiles, depth, shores)
  if (ruins === undefined) return undefined
  for (const ruin of ruins) tiles[index(ruin)] = 'ruin'

  const relics = placeRelics(rng, tiles, shores, ruins)
  if (relics === undefined) return undefined

  return { width, height, tiles, shores, ruins, relics }
}

// --- the landmass ----------------------------------------------------------

/**
 * Noise under an elliptical falloff, smoothed, then reduced to its largest
 * connected piece. The falloff is what keeps the island off the edges of the
 * grid, and the smoothing is what stops it looking like static.
 */
function growLandmass(rng: Rng): boolean[] | undefined {
  const centreX = (width - 1) / 2
  const centreY = (height - 1) / 2
  let land = new Array<boolean>(TILE_COUNT).fill(false)

  for (let tile = 0; tile < TILE_COUNT; tile += 1) {
    const { x, y } = position(tile)
    // The border is always water, so a shore is never against the grid edge.
    if (x === 0 || y === 0 || x === width - 1 || y === height - 1) continue
    const nx = (x - centreX) / (width / 2)
    const ny = (y - centreY) / (height / 2)
    const radius = Math.hypot(nx, ny)
    land[tile] = rng.next() < 1.45 - 1.2 * radius
  }

  for (let pass = 0; pass < 4; pass += 1) land = smooth(land)

  const largest = largestComponent(land)
  if (largest === undefined) return undefined
  if (largest.size < LAND_RANGE.min || largest.size > LAND_RANGE.max) return undefined

  const only = new Array<boolean>(TILE_COUNT).fill(false)
  for (const tile of largest) only[tile] = true
  return only
}

/** One cellular-automaton pass: crowded tiles become land, lonely ones water. */
function smooth(land: readonly boolean[]): boolean[] {
  const next = new Array<boolean>(TILE_COUNT).fill(false)
  for (let tile = 0; tile < TILE_COUNT; tile += 1) {
    const at = position(tile)
    if (at.x === 0 || at.y === 0 || at.x === width - 1 || at.y === height - 1) continue
    let count = 0
    for (let dy = -1; dy <= 1; dy += 1) {
      for (let dx = -1; dx <= 1; dx += 1) {
        if (dx === 0 && dy === 0) continue
        const neighbour = { x: at.x + dx, y: at.y + dy }
        if (inBounds(neighbour) && land[index(neighbour)]) count += 1
      }
    }
    next[tile] = count >= 5 ? true : count <= 2 ? false : (land[tile] ?? false)
  }
  return next
}

function largestComponent(land: readonly boolean[]): Set<number> | undefined {
  const seen = new Set<number>()
  let largest: Set<number> | undefined
  for (let start = 0; start < TILE_COUNT; start += 1) {
    if (!land[start] || seen.has(start)) continue
    const component = new Set<number>([start])
    const queue = [start]
    seen.add(start)
    while (queue.length > 0) {
      const tile = queue.pop()!
      for (const neighbour of neighbours(position(tile))) {
        const at = index(neighbour)
        if (!land[at] || seen.has(at)) continue
        seen.add(at)
        component.add(at)
        queue.push(at)
      }
    }
    if (largest === undefined || component.size > largest.size) largest = component
  }
  return largest
}

/** Orthogonal distance from the sea: 1 on the coast, higher inland. */
function distanceFromWater(land: readonly boolean[]): number[] {
  const depth = new Array<number>(TILE_COUNT).fill(0)
  let frontier: number[] = []
  for (let tile = 0; tile < TILE_COUNT; tile += 1) {
    if (!land[tile]) continue
    const coastal = neighbours(position(tile)).some((neighbour) => !land[index(neighbour)])
    if (coastal) {
      depth[tile] = 1
      frontier.push(tile)
    }
  }
  let distance = 1
  while (frontier.length > 0) {
    const next: number[] = []
    for (const tile of frontier) {
      for (const neighbour of neighbours(position(tile))) {
        const at = index(neighbour)
        if (!land[at] || depth[at] !== 0) continue
        depth[at] = distance + 1
        next.push(at)
      }
    }
    frontier = next
    distance += 1
  }
  return depth
}

/**
 * Terrain by how far inland a tile sits, jittered so the island is not a set
 * of concentric rings. Mountains are worth having: they are the only tile that
 * widens sight (Q2.1), so one is forced if the noise produced none.
 */
function paintTerrain(rng: Rng, land: readonly boolean[], depth: readonly number[]): Terrain[] {
  const tiles = new Array<Terrain>(TILE_COUNT).fill('water')
  const elevation = new Array<number>(TILE_COUNT).fill(0)
  let highest = -1

  for (let tile = 0; tile < TILE_COUNT; tile += 1) {
    if (!land[tile]) continue
    const value = depth[tile]! + rng.next() * 1.6 - 0.8
    elevation[tile] = value
    tiles[tile] = value < 1.4 ? 'sand' : value < 2.1 ? 'grass' : value < 2.8 ? 'forest' : 'mountain'
    if (highest === -1 || value > elevation[highest]!) highest = tile
  }

  if (!tiles.includes('mountain') && highest !== -1) tiles[highest] = 'mountain'
  return tiles
}

// --- shores ----------------------------------------------------------------

/**
 * Landing shores: coastal sand, at least `shoreSeparation` apart, so two
 * players landing on different shores really do start on different sides of
 * the island. Greedy over a shuffled candidate list, retried a few times
 * because a poor first pick can strand the third shore.
 */
function chooseShores(rng: Rng, tiles: readonly Terrain[]): Position[] | undefined {
  const candidates: Position[] = []
  for (let tile = 0; tile < TILE_COUNT; tile += 1) {
    if (tiles[tile] !== 'sand') continue
    const at = position(tile)
    if (neighbours(at).some((neighbour) => tiles[index(neighbour)] === 'water')) candidates.push(at)
  }
  if (candidates.length < SHORE_RULE.min) return undefined

  let best: Position[] = []
  for (let round = 0; round < 6; round += 1) {
    const chosen: Position[] = []
    for (const candidate of rng.shuffle(candidates)) {
      if (chosen.every((shore) => manhattan(shore, candidate) >= shoreSeparation)) {
        chosen.push(candidate)
      }
    }
    if (chosen.length > best.length) best = chosen
    if (best.length >= SHORE_RULE.min) break
  }
  return best.length >= SHORE_RULE.min ? best : undefined
}

// --- ruins -----------------------------------------------------------------

/** Ruins sit inland and never on top of each other. */
function placeRuins(
  rng: Rng,
  tiles: readonly Terrain[],
  depth: readonly number[],
  shores: readonly Position[],
): Position[] | undefined {
  const wanted = RUIN_RANGE.min + rng.int(RUIN_RANGE.max - RUIN_RANGE.min + 1)
  const candidates = rng.shuffle(
    [...tiles.keys()].filter(
      (tile) =>
        tiles[tile] !== 'water' &&
        depth[tile]! >= 2 &&
        shores.every((shore) => manhattan(shore, position(tile)) > 1),
    ),
  )

  const chosen: Position[] = []
  for (const tile of candidates) {
    if (chosen.length === wanted) break
    const at = position(tile)
    if (chosen.every((ruin) => chebyshev(ruin, at) >= 2)) chosen.push(at)
  }
  return chosen.length === wanted ? chosen : undefined
}

// --- relics ----------------------------------------------------------------

/**
 * Relics, placed so that no single landing can collect them all in a day.
 *
 * This is the guarantee that makes the letters worth writing: if one shore
 * could take everything, nobody would need to be told where anything is.
 *
 * "Collect" is the operative word, and it is a reading of the rule rather than
 * its letter -- see Q13.1. On a 20x12 grid where sand and grass cost 1, the
 * cheapest path from any shore to any single tile is under a day's stamina on
 * essentially every island, so a guarantee about individual tiles could not be
 * met at all. A guarantee about the *round* -- the cheapest walk from a shore
 * that touches every relic -- is the one that has the effect the rule is after.
 *
 * Relics are placed by farthest-point sampling over walking cost, which spreads
 * them across the island and makes that round long by construction rather than
 * by rejection.
 */
function placeRelics(
  rng: Rng,
  tiles: readonly Terrain[],
  shores: readonly Position[],
  ruins: readonly Position[],
): Position[] | undefined {
  const taken = new Set([...ruins, ...shores].map(index))
  const candidates = [...tiles.keys()].filter(
    (tile) => tiles[tile] !== 'water' && tiles[tile] !== 'ruin' && !taken.has(tile),
  )
  if (candidates.length < RELIC_RANGE.min) return undefined

  const wanted = RELIC_RANGE.min + rng.int(RELIC_RANGE.max - RELIC_RANGE.min + 1)
  const chosen = [rng.pick(candidates)]
  let spread = costsFrom(tiles, position(chosen[0]!))

  while (chosen.length < wanted) {
    let bestTile = -1
    let bestDistance = -1
    // Shuffled, so a tie between equally distant tiles is broken by the seed.
    for (const tile of rng.shuffle(candidates)) {
      if (chosen.includes(tile)) continue
      const distance = spread.get(tile) ?? Number.POSITIVE_INFINITY
      if (distance > bestDistance) {
        bestDistance = distance
        bestTile = tile
      }
    }
    if (bestTile === -1) return undefined
    chosen.push(bestTile)
    const added = costsFrom(tiles, position(bestTile))
    const nearer = new Map<number, number>()
    for (const tile of candidates) {
      nearer.set(
        tile,
        Math.min(
          spread.get(tile) ?? Number.POSITIVE_INFINITY,
          added.get(tile) ?? Number.POSITIVE_INFINITY,
        ),
      )
    }
    spread = nearer
  }

  const relics = chosen.map(position)
  for (const shore of shores) {
    if (cheapestRound(tiles, shore, relics) <= GAME_CONFIG.stamina.live) return undefined
  }
  return relics
}

/** Cheapest walking cost from one tile to every tile it can reach. */
function costsFrom(tiles: readonly Terrain[], from: Position): ReachMap {
  return reachableWithin(tiles, from, null)
}

/**
 * The cheapest walk from `from` that touches every relic, in any order and
 * without returning. Six relics is 720 orders, which is nothing here and is
 * only ever computed while generating.
 */
export function cheapestRound(
  tiles: readonly Terrain[],
  from: Position,
  relics: readonly Position[],
): number {
  if (relics.length === 0) return 0
  const points = [from, ...relics]
  const costs = points.map((point) => costsFrom(tiles, point))
  const between = (a: number, b: number) =>
    costs[a]!.get(index(points[b]!)) ?? Number.POSITIVE_INFINITY

  let cheapest = Number.POSITIVE_INFINITY
  const walk = (at: number, left: number[], sofar: number) => {
    if (sofar >= cheapest) return
    if (left.length === 0) {
      cheapest = sofar
      return
    }
    for (const [position_, next] of left.entries()) {
      walk(
        next,
        [...left.slice(0, position_), ...left.slice(position_ + 1)],
        sofar + between(at, next),
      )
    }
  }
  walk(
    0,
    relics.map((_relic, at) => at + 1),
    0,
  )
  return cheapest
}
